import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../components/ui';
import {
  Activity,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Key,
  LayoutDashboard,
  Lock,
  RefreshCw,
  Search,
  Server,
  Settings,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { themedConfirm } from '../lib/themed-dialogs';

// ── Types ────────────────────────────────────────────────────────────────

interface LoggingConfigFile {
  path: string;
  exists: boolean;
  size?: number;
  modified_at?: string;
  retention?: string;
  endpoints?: string[];
  jobs?: string[];
  sources?: string[];
  error?: string;
}

interface LoggingService {
  id: 'core_loki' | 'audit_loki' | 'alloy';
  label: string;
  container: string;
  status: 'running' | 'stopped' | 'not_installed' | 'unavailable';
  image?: string;
  endpoint?: string;
  config: LoggingConfigFile;
}

interface NodeLoggingSnapshot {
  node_id: string;
  observed_at: string;
  owner: 'acm-self';
  services: Record<'core_loki' | 'audit_loki' | 'alloy', LoggingService>;
  streams: Array<{ job: string; source: 'core' | 'audit' }>;
}

interface AuditEntry {
  ts: string;
  category: string;
  event: string;
  identity?: { type: string; id: string };
  ip?: string | null;
  ua?: string | null;
  data?: Record<string, unknown>;
}

interface AuditResponse {
  total: number;
  entries: AuditEntry[];
}

// ── DASHBOARD TAB ────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['logy-node-config'],
    queryFn: () => api.mcp<NodeLoggingSnapshot>('acm-self.logging_config', {}),
    refetchInterval: 30_000,
  });
  const core = data?.services.core_loki;
  const audit = data?.services.audit_loki;
  const alloy = data?.services.alloy;
  const statusValue = (service?: LoggingService) =>
    service?.status === 'running' ? t('logs.statusRunning', 'Běží') : service?.status ?? '—';

  return (
    <div className='space-y-6'>
      <StatsGrid columns={4}>
        <StatCard
          icon={Server}
          label='ACM Core Loki'
          value={statusValue(core)}
          sub={core?.endpoint ?? '127.0.0.1:3100'}
          variant={core?.status === 'running' ? 'success' : 'warning'}
        />
        <StatCard
          icon={ShieldAlert}
          label='ACM Audit Loki'
          value={statusValue(audit)}
          sub={audit?.endpoint ?? '127.0.0.1:3101'}
          variant={audit?.status === 'running' ? 'success' : 'warning'}
        />
        <StatCard
          icon={Activity}
          label='ACM Alloy scraper'
          value={statusValue(alloy)}
          sub={alloy?.image ?? 'acm-core-alloy'}
          variant={alloy?.status === 'running' ? 'success' : 'warning'}
        />
        <StatCard
          icon={RefreshCw}
          label={t('logs.nodeLabel', 'ACM node')}
          value={data?.node_id ?? '—'}
          sub={data?.observed_at ? new Date(data.observed_at).toLocaleTimeString('cs') : '—'}
          variant='info'
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle icon={Server}>
            {t('logs.nodeStackTitle', 'Logovací stack tohoto nodu')}
          </CardTitle>
        </CardHeader>
        <CardContent className='divide-y divide-gray-800'>
          {[core, audit, alloy].filter(Boolean).map((service) => (
            <div key={service!.id} className='grid gap-2 py-3 text-sm md:grid-cols-[180px_1fr_1fr]'>
              <span className='font-medium text-white'>{service!.label}</span>
              <code className='text-xs text-blue-400'>{service!.container}</code>
              <code className='break-all text-xs text-gray-500'>{service!.config.path}</code>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className='rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 text-xs text-gray-400'>
        {t(
          'logs.acmSelfOwnerNote',
          'Jde o pevnou core službu ACM nodu. Instalaci a reconciliaci vlastní acm-self.',
        )}
      </div>
    </div>
  );
}

// ── AUDIT LOG TAB ────────────────────────────────────────────────────────

const AUDIT_CATEGORIES = [
  { id: '', tKey: 'logs.categoryAll', fallback: 'Vše', icon: ShieldAlert },
  { id: 'key', tKey: 'logs.categoryApiKeys', fallback: 'API klíče', icon: Key },
  { id: 'auth', tKey: 'logs.categoryAuth', fallback: 'Auth', icon: Lock },
  { id: 'acl', tKey: 'logs.categoryAcl', fallback: 'ACL', icon: Users },
  { id: 'admin', tKey: 'logs.categoryAdmin', fallback: 'Admin', icon: Settings },
];

const CATEGORY_COLORS: Record<string, string> = {
  key: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  auth: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  acl: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  admin: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const EVENT_COLORS: Record<string, string> = {
  verify_ok: 'text-green-400',
  verify_fail: 'text-red-400',
  login_ok: 'text-green-400',
  login_fail: 'text-red-400',
  key_created: 'text-blue-400',
  key_deleted: 'text-red-400',
  key_rotated: 'text-amber-400',
  acl_updated: 'text-purple-400',
};

const LIMIT = 100;

function AuditLogTab() {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [offset, setOffset] = useState(0);
  const [showSystem, setShowSystem] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery<AuditResponse>({
    queryKey: ['logy-audit', category, search, offset, showSystem],
    queryFn: () =>
      api.mcp<AuditResponse>('acm-self.audit_log', {
        category: category || undefined,
        search: search || undefined,
        limit: LIMIT,
        offset,
        system: showSystem || undefined,
      }),
    refetchInterval: 30_000,
  });

  const handleSearch = useCallback(() => {
    setSearch(searchInput);
    setOffset(0);
  }, [searchInput]);

  const handleCategory = (cat: string) => {
    setCategory(cat);
    setOffset(0);
  };

  const total = data?.total ?? 0;
  const entries = data?.entries ?? [];
  const pages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div className='space-y-4'>
      {/* Filters */}
      <div className='flex flex-wrap items-center gap-3'>
        <div className='flex gap-1.5 flex-wrap'>
          {AUDIT_CATEGORIES.map(({ id, tKey, fallback, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleCategory(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all ${
                category === id
                  ? 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                  : 'bg-gray-900/40 border-gray-700/50 text-gray-400 hover:text-white hover:border-gray-600'
              }`}
            >
              <Icon size={12} />
              {t(tKey, fallback)}
            </button>
          ))}
        </div>

        <div className='flex gap-2 ml-auto items-center'>
          {/* Periodický šum (reconcile smyčky) default skrytý */}
          <label className='flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none'>
            <input
              type='checkbox'
              checked={showSystem}
              onChange={(e) => {
                setShowSystem(e.target.checked);
                setOffset(0);
              }}
              className='accent-blue-600'
            />
            {t('logs.showSystem', 'vč. systémových')}
          </label>
          <div className='relative'>
            <Search size={13} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500' />
            <input
              className='bg-gray-900/60 border border-gray-700/60 rounded-xl pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 w-56'
              placeholder={t('logs.searchPlaceholder', 'Hledat...')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            onClick={handleSearch}
            className='px-3 py-1.5 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-300 text-sm hover:bg-blue-600/30 transition-colors'
          >
            {t('logs.searchButton', 'Hledat')}
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className='p-1.5 rounded-xl border border-gray-700/60 text-gray-500 hover:text-white hover:border-gray-600 transition-colors disabled:opacity-40'
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Count + pagination */}
      {!isLoading && (
        <div className='flex items-center justify-between text-xs text-gray-500'>
          <span>{t('logs.auditRecordsTotal', { count: total })}</span>
          {pages > 1 && (
            <div className='flex items-center gap-2'>
              <button
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                className='p-1 rounded-lg hover:text-white disabled:opacity-30 transition-colors'
              >
                <ChevronLeft size={14} />
              </button>
              <span>
                {t('logs.pageOf', 'strana {{current}} / {{total}}', {
                  current: currentPage,
                  total: pages,
                })}
              </span>
              <button
                onClick={() => setOffset(offset + LIMIT)}
                disabled={currentPage >= pages}
                className='p-1 rounded-lg hover:text-white disabled:opacity-30 transition-colors'
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className='p-0'>
          {isLoading
            ? (
              <div className='p-4 space-y-1'>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className='h-10 bg-gray-800/40 rounded-xl animate-pulse' />
                ))}
              </div>
            )
            : entries.length === 0
            ? (
              <div className='py-16 text-center text-gray-600 text-sm'>
                {t('logs.noRecords', 'Žádné záznamy')}
              </div>
            )
            : (
              <div className='divide-y divide-gray-800/60'>
                {entries.map((e, i) => <AuditRow key={i} entry={e} />)}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const catColor = CATEGORY_COLORS[entry.category] ??
    'text-gray-400 bg-gray-500/10 border-gray-500/20';
  const evColor = EVENT_COLORS[entry.event] ?? 'text-gray-300';
  const d = new Date(entry.ts);
  const ts = d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'medium' });

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className='w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/30 transition-colors text-left'
      >
        <span className='text-xs text-gray-500 font-mono w-36 flex-shrink-0'>{ts}</span>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full border font-medium w-16 text-center flex-shrink-0 ${catColor}`}
        >
          {entry.category}
        </span>
        <span className={`text-sm font-mono font-medium flex-shrink-0 ${evColor}`}>
          {entry.event}
        </span>
        <span className='text-xs text-gray-500 flex-1 truncate font-mono'>
          {entry.identity ? `${entry.identity.type}:${entry.identity.id}` : '—'}
        </span>
        {entry.ip && (
          <span className='text-xs text-gray-600 font-mono flex-shrink-0'>{entry.ip}</span>
        )}
        {entry.data && Object.keys(entry.data).length > 0 && (
          <span className='text-[10px] text-gray-600 flex-shrink-0'>{open ? '▲' : '▼'}</span>
        )}
      </button>
      {open && entry.data && (
        <div className='px-4 pb-3 bg-gray-950/40'>
          <pre className='acm-terminal text-xs rounded-xl p-3 overflow-x-auto whitespace-pre-wrap'>
            {JSON.stringify(entry.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── SETTINGS TAB ─────────────────────────────────────────────────────────

function SettingsTab() {
  const { t } = useTranslation();
  const { data: cfg, isLoading } = useQuery({
    queryKey: ['logy-node-config'],
    queryFn: () => api.mcp<NodeLoggingSnapshot>('acm-self.logging_config', {}),
  });

  if (isLoading) return <p className='text-gray-600 text-sm'>{t('logs.loading', 'Načítám…')}</p>;

  return (
    <div className='space-y-6'>
      {cfg && Object.values(cfg.services).map((service) => (
        <Card key={service.id}>
          <CardHeader>
            <CardTitle icon={service.id === 'audit_loki' ? ShieldAlert : Server}>
              {service.label}
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            <div className='flex items-center justify-between gap-4'>
              <span className='text-gray-500'>{t('logs.statusLabelColon', 'Stav')}</span>
              <span className={service.status === 'running' ? 'text-green-400' : 'text-amber-400'}>
                {service.status}
              </span>
            </div>
            <div className='flex items-center justify-between gap-4'>
              <span className='text-gray-500'>{t('logs.containerLabel', 'Kontejner')}</span>
              <code className='text-xs text-blue-400'>{service.container}</code>
            </div>
            {service.endpoint && (
              <div className='flex items-center justify-between gap-4'>
                <span className='text-gray-500'>{t('logs.urlLabel', 'Lokální endpoint')}</span>
                <code className='text-xs text-blue-400'>{service.endpoint}</code>
              </div>
            )}
            <div className='flex items-center justify-between gap-4'>
              <span className='text-gray-500'>{t('logs.configLabelColon', 'Konfigurace')}</span>
              <code className='break-all text-right text-xs text-gray-300'>
                {service.config.path}
              </code>
            </div>
            <div className='flex items-center justify-between gap-4'>
              <span className='text-gray-500'>{t('logs.configState', 'Soubor')}</span>
              <span className={service.config.exists ? 'text-green-400' : 'text-red-400'}>
                {service.config.exists
                  ? `${service.config.size ?? 0} B · ${
                    service.config.modified_at
                      ? new Date(service.config.modified_at).toLocaleString('cs')
                      : '—'
                  }`
                  : t('logs.configMissing', 'chybí')}
              </span>
            </div>
            {service.config.retention && (
              <div className='flex items-center justify-between gap-4'>
                <span className='text-gray-500'>{t('logs.retentionLabel', 'Retence')}</span>
                <code className='text-xs text-white'>{service.config.retention}</code>
              </div>
            )}
            {(service.config.endpoints ?? []).map((endpoint) => (
              <div key={endpoint} className='flex items-center justify-between gap-4'>
                <span className='text-gray-500'>{t('logs.pushTarget', 'Push cíl')}</span>
                <code className='break-all text-right text-xs text-white'>{endpoint}</code>
              </div>
            ))}
            {(service.config.sources ?? []).map((source) => (
              <div key={source} className='flex items-center justify-between gap-4'>
                <span className='text-gray-500'>{t('logs.sourcePath', 'Zdroj')}</span>
                <code className='break-all text-right text-xs text-white'>{source}</code>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <div className='rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 text-xs text-gray-400'>
        {t(
          'logs.configReadOnlyNote',
          'Zobrazené hodnoty jsou načtené přímo z tohoto ACM nodu. Změny pevného core stacku provádí acm-self, ne obecný Docker nástroj.',
        )}
      </div>
    </div>
  );
}

// ── LOG STREAMS TAB — live Loki viewer ───────────────────────────────────

interface LokiEntry {
  ts: string;
  line: string;
}

function LogStreamsTab() {
  const { t } = useTranslation();
  const [selectedStream, setSelectedStream] = useState('audit:acm-audit');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [minutes, setMinutes] = useState(30);

  const { data: jobsData } = useQuery<Array<{ name: string; source: 'core' | 'audit' }>>({
    queryKey: ['loki-jobs'],
    queryFn: () =>
      api.mcp<{ jobs: Array<{ name: string; source: 'core' | 'audit' }> }>('acm-self.loki_jobs', {})
        .then((d) => d.jobs ?? []),
    staleTime: 60_000,
  });
  const jobs = jobsData && jobsData.length > 0
    ? jobsData
    : [{ name: 'acm-audit', source: 'audit' as const }];

  useEffect(() => {
    if (!jobsData || jobsData.length === 0) return;
    if (!jobsData.some((candidate) => `${candidate.source}:${candidate.name}` === selectedStream)) {
      setSelectedStream(`${jobsData[0]!.source}:${jobsData[0]!.name}`);
    }
  }, [jobsData, selectedStream]);
  const selected =
    jobs.find((candidate) => `${candidate.source}:${candidate.name}` === selectedStream) ??
      jobs[0]!;
  const { source, name: job } = selected;

  const { data, isLoading, isFetching, refetch } = useQuery<
    { ok: boolean; entries: LokiEntry[]; total: number }
  >({
    queryKey: ['loki-logs', source, job, search, minutes],
    queryFn: () =>
      api.mcp('acm-self.loki_query', {
        source,
        job,
        minutes,
        limit: 300,
        search: search || undefined,
      }),
    refetchInterval: 15_000,
  });

  const entries = data?.entries ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-3'>
        {/* Job filter — dynamicky z Loki */}
        <div className='flex gap-1 flex-wrap'>
          {jobs.map((candidate) => (
            <button
              key={`${candidate.source}:${candidate.name}`}
              onClick={() => setSelectedStream(`${candidate.source}:${candidate.name}`)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                selectedStream === `${candidate.source}:${candidate.name}`
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {candidate.name} · {candidate.source}
            </button>
          ))}
        </div>

        {/* Time range */}
        <div className='flex gap-1'>
          {[15, 30, 60, 180].map((m) => (
            <button
              key={m}
              onClick={() => setMinutes(m)}
              className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors ${
                minutes === m
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-800 text-gray-500 hover:bg-gray-700'
              }`}
            >
              {m < 60 ? `${m}m` : `${m / 60}h`}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className='flex gap-2 ml-auto items-center'>
          <div className='relative'>
            <Search size={13} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500' />
            <input
              className='bg-gray-900/60 border border-gray-700/60 rounded-xl pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 w-56'
              placeholder={t('logs.filterPlaceholderExample', 'Filtr... (např. tool_call)')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSearch(searchInput);
              }}
            />
          </div>
          <button
            onClick={() => setSearch(searchInput)}
            className='px-3 py-1.5 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-300 text-sm hover:bg-blue-600/30 transition-colors'
          >
            {t('logs.filterButton', 'Filtr')}
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className='p-1.5 rounded-xl border border-gray-700/60 text-gray-500 hover:text-white transition-colors disabled:opacity-40'
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className='text-xs text-gray-500'>
        {t('logs.recordsCount', { count: data?.total ?? 0 })}
      </div>

      <div className='acm-terminal rounded-xl p-3 text-xs space-y-0.5 max-h-[60vh] overflow-y-auto'>
        {isLoading && (
          <div className='text-gray-500 py-4 text-center'>{t('logs.loading', 'Načítám…')}</div>
        )}
        {!isLoading && entries.length === 0 && (
          <div className='text-gray-600 py-4 text-center'>
            {t('logs.noLogsForJob', 'Žádné logy pro {{job}} za posledních {{minutes}} minut', {
              job,
              minutes,
            })}
          </div>
        )}
        {entries.map((e, i) => (
          <div key={i} className='flex gap-3 hover:bg-gray-900 px-1 py-0.5 rounded group'>
            <span className='text-gray-600 flex-shrink-0 select-none'>
              {new Date(e.ts).toLocaleTimeString('cs')}
            </span>
            <span
              className={`flex-1 break-all whitespace-pre-wrap ${
                e.line.includes('error') || e.line.includes('Error')
                  ? 'text-red-400'
                  : e.line.includes('tool_call')
                  ? 'text-blue-300'
                  : e.line.includes('tool_result')
                  ? 'text-green-300'
                  : e.line.includes('chat start') || e.line.includes('chat end')
                  ? 'text-yellow-300'
                  : 'text-gray-300'
              }`}
            >
              {e.line}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ERRORS TAB — audit log chyb z DB (jen admin může mazat) ──────────────

interface ErrorEntry {
  id: number;
  ts: string;
  category: string;
  event: string;
  identity: unknown;
  data: unknown;
  deletable?: boolean;
}

function ErrorsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery<{ total: number; entries: ErrorEntry[] }>({
    queryKey: ['audit-errors'],
    queryFn: () => api.mcp('acm-self.audit_errors', { action: 'list', limit: 100 }),
    refetchInterval: 30_000,
  });

  const deleteAll = useMutation({
    mutationFn: () =>
      api.mcp<{ deleted: number }>('acm-self.audit_errors', { action: 'delete_all' }),
    onSuccess: (r: { deleted: number }) => {
      toast.success(t('logs.deletedCount', 'Smazáno {{count}} záznamů', { count: r.deleted }));
      qc.invalidateQueries({ queryKey: ['audit-errors'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteOne = useMutation({
    mutationFn: (id: number) => api.mcp('acm-self.audit_errors', { action: 'delete_one', id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audit-errors'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const entries = data?.entries ?? [];
  const hasDeletableEntries = entries.some((entry) => entry.deletable === true);

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <span className='text-xs text-gray-500'>
          {t('logs.errorRecordsCount', '{{count}} chybových záznamů (z DB)', {
            count: data?.total ?? 0,
          })}
        </span>
        <div className='flex gap-2'>
          <button
            onClick={() => refetch()}
            className='p-1.5 rounded-xl border border-gray-700/60 text-gray-500 hover:text-white transition-colors'
          >
            <RefreshCw size={14} />
          </button>
          {hasDeletableEntries && (
            <button
              onClick={async () => {
                if (await themedConfirm(t('logs.confirmDeleteAllErrors', 'Smazat všechny chybové záznamy?'))) {
                  deleteAll.mutate();
                }
              }}
              className='flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors'
            >
              <AlertTriangle size={11} /> {t('logs.deleteAll', 'Smazat vše')}
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className='text-gray-500 text-sm text-center py-8'>
          {t('logs.loading', 'Načítám…')}
        </div>
      )}
      {!isLoading && entries.length === 0 && (
        <div className='text-gray-600 text-sm text-center py-8'>
          {t('logs.noErrorRecords', 'Žádné chybové záznamy')}
        </div>
      )}

      <div className='space-y-2'>
        {entries.map((e) => (
          <div key={e.id} className='bg-gray-900 border border-red-500/10 rounded-xl p-3 space-y-1'>
            <div className='flex items-center gap-3'>
              <AlertTriangle size={12} className='text-red-400 flex-shrink-0' />
              <span className='font-mono text-xs text-red-300 flex-1'>{e.event}</span>
              <span className='text-xs text-gray-600'>{new Date(e.ts).toLocaleString('cs')}</span>
              {e.deletable === true && (
                <button
                  onClick={() => deleteOne.mutate(e.id)}
                  className='text-gray-600 hover:text-red-400 transition-colors text-xs px-1'
                >
                  ✕
                </button>
              )}
            </div>
            {e.data && Object.keys(e.data as object).length > 0 && (
              <pre className='text-xs text-gray-500 font-mono pl-5 whitespace-pre-wrap break-all'>
                {JSON.stringify(e.data, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────────────────

export default function Logy() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('logs.pageTitle', 'Logy')}
        description={t(
          'logs.pageDescription',
          'Interní logování ACM nodu — Loki, Alloy a auditní stopa',
        )}
        icon={Server}
      />

      <Tabs defaultTab='streams'>
        <TabList>
          <Tab id='streams' icon={<Activity size={14} />}>
            {t('logs.tabStreams', 'Log streamy')}
          </Tab>
          <Tab id='errors' icon={<AlertTriangle size={14} />}>{t('logs.tabErrors', 'Chyby')}</Tab>
          <Tab id='audit' icon={<ShieldAlert size={14} />}>{t('logs.tabAudit', 'Audit log')}</Tab>
          <Tab id='dashboard' icon={<LayoutDashboard size={14} />}>
            {t('logs.tabDashboard', 'Přehled')}
          </Tab>
          <Tab id='settings' icon={<Settings size={14} />}>
            {t('logs.tabSettings', 'Nastavení')}
          </Tab>
        </TabList>

        <div className='mt-6'>
          <TabPanel id='streams'>
            <LogStreamsTab />
          </TabPanel>
          <TabPanel id='errors'>
            <ErrorsTab />
          </TabPanel>
          <TabPanel id='audit'>
            <AuditLogTab />
          </TabPanel>
          <TabPanel id='dashboard'>
            <DashboardTab />
          </TabPanel>
          <TabPanel id='settings'>
            <SettingsTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
